import type {
  AuctionLogEntry,
  HiddenAuctionState,
  HiddenAuctionViewerState,
  MinusAuctionState,
  MinusAuctionViewerPlayer,
  MinusAuctionViewerState,
} from "./types";

export interface MinusAuctionProjectionOptions {
  readonly revealHiddenAuctionCubeToObservers?: boolean;
}

export function filterStateForViewer(
  state: MinusAuctionState,
  viewerUserId: string,
  options: MinusAuctionProjectionOptions = {},
): MinusAuctionViewerState {
  const viewerPlayerId = state.playerOrder.includes(viewerUserId)
    ? viewerUserId
    : null;
  const viewerRole = viewerPlayerId === null ? "observer" : "player";

  return {
    gameId: state.gameId,
    status: state.status,
    phase: state.phase,
    playerOrder: state.playerOrder,
    players: state.players.map((player) =>
      projectPlayer(state, player.playerId, viewerPlayerId),
    ),
    hostPlayerId: state.hostPlayerId,
    viewerPlayerId,
    viewerRole,
    secondHiddenCubeIncluded: state.secondHiddenCubeIncluded,
    hiddenAuctionResolved: state.hiddenAuctionResolved,
    acceptedCubeCount: state.acceptedCubeCount,
    auction: state.auction,
    hiddenAuction: projectHiddenAuction(state.hiddenAuction, viewerPlayerId, {
      revealHiddenAuctionCubeToObserver:
        viewerRole === "observer" &&
        options.revealHiddenAuctionCubeToObservers === true,
    }),
    transferLog: state.transferLog,
    auctionLog: redactAuctionLog(state.auctionLog),
    scores: state.scores,
    winnerIds: state.winnerIds,
    eliminationCandidateIds: state.eliminationCandidateIds,
    redactedFields: [
      "serverSetup",
      "drawPile",
      "hiddenTiles",
      "nonViewerResources",
      "nonViewerLocks",
    ],
  };
}

export function buildViewerStates(
  state: MinusAuctionState,
  observerUserIds: readonly string[] = [],
  options: MinusAuctionProjectionOptions = {},
): Readonly<Record<string, MinusAuctionViewerState>> {
  const recipients = [...new Set([...state.playerOrder, ...observerUserIds])];
  return Object.fromEntries(
    recipients.map((recipientUserId) => [
      recipientUserId,
      filterStateForViewer(state, recipientUserId, options),
    ]),
  );
}

function projectPlayer(
  state: MinusAuctionState,
  playerId: string,
  viewerPlayerId: string | null,
): MinusAuctionViewerPlayer {
  const player = state.players.find(
    (candidate) => candidate.playerId === playerId,
  );
  if (!player) {
    throw new Error(`Unknown player: ${playerId}`);
  }
  const isSelf = playerId === viewerPlayerId;
  const lockedGarnets =
    state.hiddenAuction?.lockedGarnetsByPlayerId[playerId] ?? 0;

  return {
    playerId,
    chips: isSelf ? player.chips : null,
    garnets: isSelf ? player.garnets : null,
    lockedGarnets: isSelf ? lockedGarnets : null,
    availableGarnets: isSelf ? player.garnets - lockedGarnets : null,
    cubes: player.cubes,
  };
}

function projectHiddenAuction(
  hiddenAuction: HiddenAuctionState | null,
  viewerPlayerId: string | null,
  options: { readonly revealHiddenAuctionCubeToObserver: boolean },
): HiddenAuctionViewerState | null {
  if (!hiddenAuction) {
    return null;
  }
  const canSeeHiddenCube =
    viewerPlayerId === hiddenAuction.highestBidderId ||
    options.revealHiddenAuctionCubeToObserver;

  return {
    cube: canSeeHiddenCube ? hiddenAuction.cube : null,
    currentBid: hiddenAuction.currentBid,
    currentBidderId: hiddenAuction.currentBidderId,
    highestBidderId: hiddenAuction.highestBidderId,
    passedPlayerIds: hiddenAuction.passedPlayerIds,
    lastBidByPlayerId: hiddenAuction.lastBidByPlayerId,
    lockedGarnetsByPlayerId: Object.fromEntries(
      Object.entries(hiddenAuction.lockedGarnetsByPlayerId).map(
        ([playerId, locked]) => [
          playerId,
          playerId === viewerPlayerId ? locked : null,
        ],
      ),
    ),
    resumePlayerId: hiddenAuction.resumePlayerId,
  };
}

function redactAuctionLog(
  auctionLog: readonly AuctionLogEntry[],
): readonly AuctionLogEntry[] {
  return auctionLog.map((entry) =>
    entry.message.includes("히든")
      ? {
          message: entry.message,
          playerId: entry.playerId,
        }
      : entry,
  );
}
